/**
 * Inventory and seating-count validation rules — checks that don't depend
 * on spatial distance, just on totals. PRD §8.2 rows: "Inventory cap
 * exceeded" and "Seat count mismatch".
 */

import type { InventoryLimit, LayoutObject, ValidationIssue } from '../../types';
import { CHAIR_STOCK } from '../../inventory';

const STOCK_LIMITED_DISPLAY_NAMES: Record<string, string> = {
  round_table: 'round tables',
  rect_table_6: "6' rectangle tables",
  rect_table_8: "8' rectangle tables",
  dance_floor_15: "15'x15' dance floors",
  dance_floor_18: "18'x18' dance floors",
  cocktail_table: 'cocktail tables',
};

export function checkInventoryLimits(
  objects: LayoutObject[],
  inventoryLimits: InventoryLimit[]
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const limit of inventoryLimits) {
    const used = objects.filter((o) => o.type === limit.itemType).length;
    if (used > limit.maxQuantity) {
      const name = STOCK_LIMITED_DISPLAY_NAMES[limit.itemType] ?? limit.itemType;
      // Warning, not Critical — running short on tables or dance floors
      // is a real, operationally serious problem worth surfacing, but
      // it's not the same urgency as the chair-stock case below, which
      // directly means a guest has nowhere to sit. Matches the brief's
      // "Capacity approaching limits" Warning-tier example.
      issues.push({
        id: `inventory-cap-${limit.itemType}`,
        severity: 'warning',
        message: `This layout uses ${used} ${name}; only ${limit.maxQuantity} are in venue stock.`,
        relatedObjectIds: objects.filter((o) => o.type === limit.itemType).map((o) => o.id),
      });
    }
  }

  // Chairs are a single venue-wide stock number, not a catalog item type.
  const totalChairs = objects.reduce((sum, o) => sum + (o.seats ?? 0), 0);
  if (totalChairs > CHAIR_STOCK) {
    // Direct match to the brief's explicit Critical example: "Guest
    // count exceeds available chairs." Unlike running short on, say,
    // cocktail tables, this means specific guests will have nowhere to
    // sit — a real problem that would prevent setup, not a preference.
    issues.push({
      id: 'inventory-cap-chairs',
      severity: 'critical',
      message: `This layout requires ${totalChairs} chairs; only ${CHAIR_STOCK} are in venue stock.`,
      relatedObjectIds: [],
    });
  }

  return issues;
}

export function checkSeatCountMismatch(
  objects: LayoutObject[],
  guestCount: number
): ValidationIssue[] {
  const totalSeats = objects.reduce((sum, o) => sum + (o.seats ?? 0), 0);

  if (totalSeats < guestCount) {
    // Under-seated: this means specific guests have nowhere to sit —
    // functionally the same real problem as exceeding chair stock
    // (checkInventoryLimits), just expressed as "not enough seats
    // placed yet" rather than "not enough chairs exist." Critical for
    // the same reason: it would realistically prevent setup as-is.
    return [
      {
        id: 'seat-count-mismatch',
        severity: 'critical',
        message: `${guestCount} guests expected, but tables currently seat only ${totalSeats}.`,
        relatedObjectIds: [],
      },
    ];
  }

  if (totalSeats > guestCount) {
    // Over-seated: harmless slack capacity, not a problem at all — a
    // coordinator who added a couple of extra seats as a buffer, or
    // hasn't finalized the guest count yet, shouldn't see this treated
    // as anything more than a passing note.
    return [
      {
        id: 'seat-count-mismatch',
        severity: 'info',
        message: `${guestCount} guests expected; tables currently seat ${totalSeats} \u2014 a little extra room.`,
        relatedObjectIds: [],
      },
    ];
  }

  return [];
}
